import React, { useState, useEffect } from 'react';
import { Switch, Typography, Row, Col, Button, message, Tag, Tooltip, Grid, Divider } from 'antd';
import { GlassCard } from '../../shared/components';
import { useTranslation } from 'react-i18next';
import { useList, useCreate, useCustom, useApiUrl } from '@refinedev/core';
import { useColorMode } from '../../contexts/color-mode';
import { axiosInstance } from '../../authProvider';

const { Title, Text } = Typography;

/** GET response item (swagger NotificationPreferenceResponseDto) */
export type NotificationPreferenceResponseDto = {
    id: number;
    userId: number;
    category: 'order' | 'message' | 'system';
    channels: ('in_app' | 'email' | 'telegram')[];
    isEnabled: boolean;
};

/** POST body item (swagger NotificationPreferenceItemDto) */
export type NotificationPreferenceItemDto = {
    category: 'order' | 'message' | 'system';
    channels: ('in_app' | 'email' | 'telegram')[];
    isEnabled: boolean;
};

type NotificationCategory = 'order' | 'message' | 'system';
type Channel = 'Web' | 'Email' | 'Telegram';

type Preferences = {
    [key in NotificationCategory]: {
        [key in Channel]: boolean;
    };
};

const ALL_CATEGORIES: NotificationCategory[] = ['order', 'message', 'system'];

type MySettings = {
    telegram?: {
        chatId?: string | null;
    };
    [key: string]: unknown;
};

const NotificationPreferences: React.FC = () => {
    const { t } = useTranslation();
    const { mode } = useColorMode();
    const breakpoint = Grid.useBreakpoint();
    const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg;
    const apiUrl = useApiUrl();
    const { data: mySettingsResponse, refetch: refetchMySettings } = useCustom<{ data: MySettings | null }>({
        url: `${apiUrl}/users/my-settings`,
        method: "get",
    });
    
    const notificationCategories: { key: NotificationCategory; label: string; description: string }[] = [
        { key: 'order', label: t('notifications.tabs.order'), description: t('notifications.noOrderNotifications') },
        { key: 'message', label: t('notifications.tabs.message'), description: t('notifications.noMessageNotifications') },
        { key: 'system', label: t('notifications.tabs.system'), description: t('notifications.noSystemNotifications') },
    ];

    const channels: Channel[] = ['Web', 'Email', 'Telegram'];

    const defaultPreferences: Preferences = ALL_CATEGORIES.reduce<Preferences>((acc, cat) => {
        acc[cat] = { Web: true, Email: true, Telegram: false };
        return acc;
    }, {} as Preferences);

    const [preferences, setPreferences] = useState<Preferences>(defaultPreferences);
    const [isTelegramLoading, setIsTelegramLoading] = useState(false);
    const [isPollingTelegram, setIsPollingTelegram] = useState(false);
    const [telegramLinked, setTelegramLinked] = useState<boolean | null>(null);
    const [pollAttempts, setPollAttempts] = useState(0);

    const mySettings = (mySettingsResponse?.data ?? null) as MySettings | null;

    useEffect(() => {
        const chatId = mySettings?.telegram?.chatId;
        if (chatId) {
            setTelegramLinked(true);
            setIsPollingTelegram(false);
        } else if (chatId === null || chatId === undefined) {
            setTelegramLinked(false);
        }
    }, [mySettings]);

    const { data: preferencesData, isLoading: isLoadingPreferences } = useList({
        resource: 'notifications/preferences',
    });

    const { mutate: updatePreferences } = useCreate();

    useEffect(() => {
        if (!preferencesData?.data || !Array.isArray(preferencesData.data)) return;
        const list = preferencesData.data as NotificationPreferenceResponseDto[];
        const newPreferences = ALL_CATEGORIES.reduce<Preferences>((acc, cat) => {
            const item = list.find(p => p.category === cat);
            const ch = item?.channels ?? [];
            acc[cat] = {
                Web: ch.includes('in_app'),
                Email: ch.includes('email'),
                Telegram: ch.includes('telegram'),
            };
            return acc;
        }, {} as Preferences);
        setPreferences(newPreferences);
    }, [preferencesData]);

    useEffect(() => {
        let intervalId: number | undefined;

        if (telegramLinked || !isPollingTelegram) {
            return;
        }

        intervalId = window.setInterval(async () => {
            try {
                const response = await refetchMySettings();
                const latestSettings = (response?.data?.data ?? null) as MySettings | null;
                const chatId = latestSettings?.telegram?.chatId;

                if (chatId) {
                    setTelegramLinked(true);
                    setIsPollingTelegram(false);
                    if (intervalId) {
                        window.clearInterval(intervalId);
                    }
                } else {
                    setPollAttempts((prev) => prev + 1);
                }
            } catch (error) {
                setIsPollingTelegram(false);
                if (intervalId) {
                    window.clearInterval(intervalId);
                }
            }
        }, 5000);

        if (pollAttempts >= 24 && intervalId) {
            // stop after ~2 minutes
            window.clearInterval(intervalId);
            setIsPollingTelegram(false);
        }

        return () => {
            if (intervalId) {
                window.clearInterval(intervalId);
            }
        };
    }, [isPollingTelegram, telegramLinked, pollAttempts, refetchMySettings]);

    const handleTelegramConnect = async () => {
        try {
            setIsTelegramLoading(true);
            const response = await axiosInstance.get(`${apiUrl}/auth/telegram/connect-url`);
            const url = response.data?.url;

            if (url) {
                setIsPollingTelegram(true);
                window.open(url, "_blank");
            } else {
                message.error(t('notifications.telegram.error'));
                setIsTelegramLoading(false);
            }
        } catch (error) {
            message.error(t('notifications.telegram.error'));
            setIsTelegramLoading(false);
        }
    };

    const handleToggle = async (category: NotificationCategory, channel: Channel) => {
        const newPreferences = {
            ...preferences,
            [category]: {
                ...preferences[category],
                [channel]: !preferences[category][channel],
            },
        };
        setPreferences(newPreferences);
        try {
            const payload: NotificationPreferenceItemDto[] = notificationCategories.map(cat => {
                const pref = newPreferences[cat.key];
                const channelsList: ('in_app' | 'email' | 'telegram')[] = [];
                if (pref.Web) channelsList.push('in_app');
                if (pref.Email) channelsList.push('email');
                if (pref.Telegram) channelsList.push('telegram');
                return {
                    category: cat.key,
                    channels: channelsList,
                    isEnabled: channelsList.length > 0,
                };
            });
            await updatePreferences({
                resource: 'notifications/preferences',
                values: payload,
            });
        } catch (error) {
            message.error(t('notifications.errorUpdatingPreferences'));
            setPreferences(preferences);
        }
    };

    const telegramSection = (
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Row align="middle" justify="space-between">
                <Col span={12}>
                    <Text strong className={mode === 'dark' ? 'text-gray-100' : 'text-gray-800'}>
                        {t('notifications.telegram.title')}
                    </Text>
                    <div
                        className={`text-sm mt-1 ${
                            mode === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}
                    >
                        {t('notifications.telegram.description')}
                    </div>
                </Col>
                <Col span={12} className="flex justify-end items-center gap-3">
                    {telegramLinked ? (
                        <Tag color="success" className="mb-0">
                            {t('notifications.telegram.linkedStatus')}
                        </Tag>
                    ) : (
                        <Button
                            type="primary"
                            onClick={handleTelegramConnect}
                            loading={isTelegramLoading}
                            disabled={isTelegramLoading || isPollingTelegram}
                        >
                            {isPollingTelegram
                                ? t('notifications.telegram.waitingForLink')
                                : t('notifications.telegram.linkButton')}
                        </Button>
                    )}
                </Col>
            </Row>
        </div>
    );

    const content = (
        <>
            {notificationCategories.map((cat, idx) => (
                <div key={cat.key} className={isMobile ? "py-4" : "mb-8"}>
                    <Row align="middle" justify="space-between" gutter={[0, 8]}>
                        <Col xs={24} sm={8}>
                            <Text strong className={mode === 'dark' ? 'text-gray-100' : 'text-gray-800'}>
                                {cat.label}
                            </Text>
                            <div className={`text-sm mt-1 ${
                                mode === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                {cat.description}
                            </div>
                        </Col>
                        <Col xs={24} sm={16}>
                            <Row gutter={24}>
                                {channels.map(channel => {
                                    const isTelegram = channel === 'Telegram';
                                    const disabled = isTelegram && !telegramLinked;
                                    const switchEl = (
                                        <Switch
                                            loading={isLoadingPreferences}
                                            checked={preferences[cat.key][channel]}
                                            onChange={() => handleToggle(cat.key, channel)}
                                            checkedChildren={channel}
                                            unCheckedChildren={channel}
                                            className="w-[90px]"
                                            disabled={disabled}
                                        />
                                    );
                                    return (
                                        <Col key={channel}>
                                            {disabled ? (
                                                <Tooltip title={t('notifications.telegram.linkToEnable')}>
                                                    <span>{switchEl}</span>
                                                </Tooltip>
                                            ) : (
                                                switchEl
                                            )}
                                        </Col>
                                    );
                                })}
                            </Row>
                        </Col>
                    </Row>
                    {isMobile && idx < notificationCategories.length - 1 && (
                        <Divider style={{ margin: "0" }} />
                    )}
                </div>
            ))}
            {telegramSection}
        </>
    );

    return isMobile ? (
        <div className="px-1 pb-4">{content}</div>
    ) : (
        <div className="space-y-8">{content}</div>
    );
};

export default NotificationPreferences; 